// A stock item, new or edited (spec/13 §6.4 Item dialog; `inventory:manage`, or `inventory:supplies` for a supply):
// its name, kind (an ingredient or a supply — a retail item is made for each retail product and named after it), the
// unit it is counted in (fixed once it has moved), its par level in the natural unit, SKU and barcode, a note, and its
// **pack sizes** — how it arrives and is counted ("5 kg bag" = 5 kg, "Case of 24" = 24 pcs, each with its own barcode).
// The average cost is shown, read-only: it comes from deliveries. Editing sends only what changed; the packs go as a
// whole set, each kept by the id it was read with. An item counted in pieces says what a piece is called ("can",
// "cans") so every screen reads "12 cans"; its usual supplier is what a delivery of it pre-selects.

import type { CreateStockItemRequest, StockBaseUnit, StockItem, StockItemKind, StockItemPackInput, UpdateStockItemRequest } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Field, FormSection, Input, SegmentedControl, Select, SelectField, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Notice } from '../../ui';
import type { StockAccess } from './access';
import { useStockFormat } from './format';
import { useCreateStockItem, useStockSuppliers, useUpdateStockItem } from './queries';
import type { StockDialog } from './StockPage';
import { inputTextOf, inputUnitsFor, naturalInputUnit, parseQuantity, type DisplayUnit } from './units';
import { useProblem } from '../../api/use-problem';

const BASE_UNITS: readonly StockBaseUnit[] = ['each', 'g', 'ml'];

interface PackDraft {
  readonly key: string;
  readonly id?: string;
  readonly name: string;
  readonly amount: string;
  readonly unit: DisplayUnit;
  readonly barcode: string;
}

interface Draft {
  readonly name: string;
  readonly kind: Exclude<StockItemKind, 'retail'>;
  readonly baseUnit: StockBaseUnit;
  readonly sku: string;
  readonly barcode: string;
  readonly par: string;
  readonly parUnit: DisplayUnit;
  readonly note: string;
  /** What one is called, for an item counted in pieces: "can", "cans". */
  readonly unitName: string;
  readonly unitNamePlural: string;
  /** The usual supplier, which a delivery of it pre-selects; `none` for none. */
  readonly supplierId: string;
  readonly packs: readonly PackDraft[];
  readonly archived: boolean;
}

const NO_SUPPLIER = 'none';

let packKey = 0;
const nextKey = () => `pack-${(packKey += 1)}`;

function draftOf(item: StockItem | undefined, suppliesOnly: boolean): Draft {
  if (item === undefined) {
    return { name: '', kind: suppliesOnly ? 'supply' : 'ingredient', baseUnit: 'g', sku: '', barcode: '', par: '', parUnit: 'kg', note: '', unitName: '', unitNamePlural: '', supplierId: NO_SUPPLIER, packs: [], archived: false };
  }
  const typical = [...item.packs.map((pack) => pack.quantity), ...(item.parLevel === null ? [] : [item.parLevel])];
  const parUnit = naturalInputUnit(item.baseUnit, typical);
  return {
    name: item.name,
    kind: item.kind === 'supply' ? 'supply' : 'ingredient',
    baseUnit: item.baseUnit,
    sku: item.sku ?? '',
    barcode: item.barcode ?? '',
    par: inputTextOf(item.parLevel, parUnit),
    parUnit,
    note: item.note ?? '',
    unitName: item.unitName ?? '',
    unitNamePlural: item.unitNamePlural ?? '',
    supplierId: item.supplierId ?? NO_SUPPLIER,
    packs: item.packs
      .filter((pack) => !pack.archived)
      .map((pack) => {
        const unit = naturalInputUnit(item.baseUnit, [pack.quantity]);
        return { key: nextKey(), id: pack.id, name: pack.name, amount: inputTextOf(pack.quantity, unit), unit, barcode: pack.barcode ?? '' };
      }),
    archived: item.archived,
  };
}

/** The unit a new pack or par is typed in when the base unit changes: kg for grams, L for millilitres. */
const defaultUnit = (baseUnit: StockBaseUnit): DisplayUnit => naturalInputUnit(baseUnit, [1000]);

export function ItemEditorDialog({
  dialog,
  access,
  onClose,
}: {
  readonly dialog: Extract<StockDialog, { kind: 'newItem' | 'editItem' }> | null;
  readonly access: StockAccess;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const editing = dialog?.kind === 'editItem' ? dialog.item : undefined;
  return (
    <Dialog
      open={dialog !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="lg"
      dirty="auto"
      eyebrow={editing === undefined ? undefined : t(`stock.kind.${editing.kind}`)}
      title={editing === undefined ? t('stock.editor.newTitle') : t('stock.editor.editTitle', { name: editing.name })}
      description={editing === undefined ? t('stock.editor.newHint') : t('stock.editor.editHint')}
    >
      {dialog !== null ? <ItemForm key={editing?.id ?? 'new'} item={editing} access={access} onDone={onClose} /> : null}
    </Dialog>
  );
}

function ItemForm({ item, access, onDone }: { readonly item: StockItem | undefined; readonly access: StockAccess; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const stock = useStockFormat();
  const create = useCreateStockItem(venue.organizationId, venue.id);
  const suppliers = useStockSuppliers(venue.organizationId, venue.id);
  const update = useUpdateStockItem(venue.organizationId, venue.id);
  const [draft, setDraft] = useState<Draft>(() => draftOf(item, access.manageSuppliesOnly));
  const [errors, setErrors] = useState<{ name?: string; par?: string; packs?: string; form?: string }>({});
  const retail = item?.kind === 'retail';
  // The unit is fixed once the item has moved: every quantity in its history is in it.
  const unitLocked = item !== undefined && item.lastMovementAt !== null;
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  // What a piece of it is called, as typed — so "holds 24 cans" reads right while the names are being written.
  const unit = { baseUnit: draft.baseUnit, unitName: draft.unitName.trim() || null, unitNamePlural: draft.unitNamePlural.trim() || null };
  const pieces = draft.baseUnit === 'each';
  const supplier = draft.supplierId === NO_SUPPLIER ? null : draft.supplierId;

  function packsOut(): StockItemPackInput[] | string {
    const out: StockItemPackInput[] = [];
    for (const pack of draft.packs) {
      if (pack.name.trim() === '' && pack.amount.trim() === '' && pack.barcode.trim() === '') continue;
      const quantity = parseQuantity(pack.amount, pack.unit);
      if (pack.name.trim() === '' || quantity === null || quantity < 1) return t('stock.editor.packInvalid');
      out.push({ ...(pack.id === undefined ? {} : { id: pack.id }), name: pack.name.trim(), quantity, barcode: pack.barcode.trim() === '' ? null : pack.barcode.trim() });
    }
    return out;
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: typeof errors = {};
    if (!retail && draft.name.trim() === '') next.name = t('stock.editor.nameRequired');
    const par = draft.par.trim() === '' ? null : parseQuantity(draft.par, draft.parUnit);
    if (draft.par.trim() !== '' && par === null) next.par = t('stock.quantity.invalid', { unit: stock.unitName(draft.parUnit, unit) });
    const packs = packsOut();
    if (typeof packs === 'string') next.packs = packs;
    setErrors(next);
    if (Object.keys(next).length > 0 || typeof packs === 'string') return;

    if (item === undefined) {
      const body: CreateStockItemRequest = {
        name: draft.name.trim(),
        kind: draft.kind,
        baseUnit: draft.baseUnit,
        ...(draft.sku.trim() === '' ? {} : { sku: draft.sku.trim() }),
        ...(draft.barcode.trim() === '' ? {} : { barcode: draft.barcode.trim() }),
        ...(par === null ? {} : { parLevel: par }),
        ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
        ...(pieces && unit.unitName !== null ? { unitName: unit.unitName, unitNamePlural: unit.unitNamePlural ?? unit.unitName } : {}),
        ...(supplier === null ? {} : { supplierId: supplier }),
        ...(packs.length === 0 ? {} : { packs }),
      };
      create.mutate(body, {
        onSuccess: (saved) => {
          show({ tone: 'ok', title: t('stock.editor.created', { name: saved.name }) });
          onDone();
        },
        onError: (failure) => setErrors({ form: problem.text(failure, { fallback: t('stock.editor.refused') }) }),
      });
      return;
    }

    // Only what changed.
    const patch: UpdateStockItemRequest = {};
    if (!retail && draft.name.trim() !== item.name) patch.name = draft.name.trim();
    if (draft.baseUnit !== item.baseUnit) patch.baseUnit = draft.baseUnit;
    if ((draft.sku.trim() || null) !== item.sku) patch.sku = draft.sku.trim() || null;
    if ((draft.barcode.trim() || null) !== item.barcode) patch.barcode = draft.barcode.trim() || null;
    if (par !== item.parLevel) patch.parLevel = par;
    if ((draft.note.trim() || null) !== item.note) patch.note = draft.note.trim() || null;
    const names = pieces ? { unitName: unit.unitName, unitNamePlural: unit.unitName === null ? null : (unit.unitNamePlural ?? unit.unitName) } : { unitName: null, unitNamePlural: null };
    if (names.unitName !== item.unitName) patch.unitName = names.unitName;
    if (names.unitNamePlural !== item.unitNamePlural) patch.unitNamePlural = names.unitNamePlural;
    if (supplier !== item.supplierId) patch.supplierId = supplier;
    if (draft.archived !== item.archived) patch.archived = draft.archived;
    const before = JSON.stringify(item.packs.filter((pack) => !pack.archived).map((pack) => [pack.id, pack.name, pack.quantity, pack.barcode]));
    const after = JSON.stringify(packs.map((pack) => [pack.id, pack.name, pack.quantity, pack.barcode ?? null]));
    if (before !== after) patch.packs = packs;
    if (Object.keys(patch).length === 0) {
      setErrors({ form: t('stock.editor.noChanges') });
      return;
    }
    update.mutate(
      { stockItemId: item.id, body: patch },
      {
        onSuccess: (saved) => {
          show({ tone: 'ok', title: patch.archived === true ? t('stock.editor.archived', { name: saved.name }) : t('stock.editor.saved', { name: saved.name }) });
          onDone();
        },
        onError: (failure) => setErrors({ form: problem.text(failure, { fallback: t('stock.editor.refused') }) }),
      },
    );
  }

  const busy = create.isPending || update.isPending;
  return (
    <form className="console-form" aria-label={item === undefined ? t('stock.editor.newTitle') : t('stock.editor.editTitle', { name: item.name })} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      {retail ? (
        <Notice role="note" title={t('stock.editor.retailTitle')}>
          <p>{t('stock.editor.retailBody')}</p>
        </Notice>
      ) : null}
      {/* Name, kind, unit and par are the item; its codes, supplier, packs, note and archive wait behind More options. */}
      <FormSection
        forceOpen={errors.packs !== undefined}
        more={
          <>
      <div className="console-form__row">
        <TextField label={t('stock.editor.sku')} showOptional autoComplete="off" spellCheck={false} translate="no" value={draft.sku} onChange={(event) => set({ sku: event.target.value })} />
        <TextField
          label={t('stock.editor.barcode')}
          showOptional
          autoComplete="off"
          spellCheck={false}
          translate="no"
          numeric
          inputMode="numeric"
          value={draft.barcode}
          onChange={(event) => set({ barcode: event.target.value })}
          description={retail ? t('stock.editor.barcodeRetail') : t('stock.editor.barcodeHint')}
        />
      </div>
      <SelectField
        label={t('stock.editor.supplier')}
        value={draft.supplierId}
        onValueChange={(supplierId) => set({ supplierId })}
        options={[{ value: NO_SUPPLIER, label: t('stock.receive.noSupplier') }, ...(suppliers.data ?? []).filter((one) => !one.archived || one.id === item?.supplierId).map((one) => ({ value: one.id, label: one.name }))]}
        description={t('stock.editor.supplierHint')}
      />
      <PacksEditor unit={unit} packs={draft.packs} onChange={(packs) => set({ packs })} error={errors.packs} />
      <TextField label={t('stock.editor.note')} showOptional autoComplete="off" value={draft.note} onChange={(event) => set({ note: event.target.value })} />
      {item !== undefined ? <Switch checked={draft.archived} onCheckedChange={(archived) => set({ archived })} label={t('stock.editor.archive')} /> : null}
      {item !== undefined && draft.archived && !item.archived ? <p className="console-subtle">{t('stock.editor.archiveHint')}</p> : null}
          </>
        }
      >
      <TextField label={t('stock.editor.name')} autoComplete="off" required={!retail} disabled={retail} value={draft.name} placeholder={t('stock.editor.namePlaceholder')} onChange={(event) => set({ name: event.target.value })} error={errors.name} />
      {item === undefined ? (
        <div className="console-field-block">
          <SegmentedControl<Draft['kind']>
            label={t('stock.editor.kind')}
            value={draft.kind}
            onValueChange={(kind) => set({ kind })}
            options={[
              { value: 'ingredient', label: t('stock.kind.ingredient'), disabled: access.manageSuppliesOnly },
              { value: 'supply', label: t('stock.kind.supply') },
            ]}
          />
          <p className="console-subtle">{t(`stock.editor.kindHint.${draft.kind}`)}</p>
        </div>
      ) : null}
      <div className="console-field-block">
        <SegmentedControl<StockBaseUnit>
          label={t('stock.editor.baseUnit')}
          value={draft.baseUnit}
          onValueChange={(baseUnit) => set({ baseUnit, parUnit: defaultUnit(baseUnit), packs: draft.packs.map((pack) => ({ ...pack, unit: defaultUnit(baseUnit) })) })}
          options={BASE_UNITS.map((unit) => ({ value: unit, label: t(`stock.baseUnit.${unit}`), disabled: unitLocked && unit !== item?.baseUnit }))}
        />
        <p className="console-subtle">{unitLocked ? t('stock.editor.unitLocked') : t('stock.editor.unitHint')}</p>
      </div>
      {pieces ? (
        <div className="console-form__row">
          <TextField label={t('stock.editor.unitName')} showOptional autoComplete="off" value={draft.unitName} placeholder={t('stock.editor.unitNamePlaceholder')} onChange={(event) => set({ unitName: event.target.value })} description={t('stock.editor.unitNameHint')} />
          <TextField label={t('stock.editor.unitNamePlural')} showOptional autoComplete="off" value={draft.unitNamePlural} placeholder={t('stock.editor.unitNamePluralPlaceholder')} onChange={(event) => set({ unitNamePlural: event.target.value })} />
        </div>
      ) : null}
      <div className="console-form__row">
        <AmountField
          label={t('stock.editor.par')}
          description={t('stock.editor.parHint')}
          baseUnit={draft.baseUnit}
          unitOf={unit}
          amount={draft.par}
          unit={draft.parUnit}
          onChange={(par, parUnit) => set({ par, parUnit })}
          error={errors.par}
        />
        {item !== undefined && access.reports ? (
          <div className="console-stock-cost" aria-live="polite">
            <span className="clav-field__label">{t('stock.editor.averageCost')}</span>
            <span className="console-num console-stock-cost__figure">{stock.unitCost(item.averageUnitCost, item.baseUnit)}</span>
            <span className="console-subtle">{item.averageUnitCost === null ? t('stock.editor.notCosted') : t('stock.editor.costHint')}</span>
          </div>
        ) : null}
      </div>
      </FormSection>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={busy}>
          {item === undefined ? t('stock.editor.create') : t('stock.editor.save')}
        </Button>
      </div>
    </form>
  );
}

/** An amount and its unit side by side: "5" "kg". */
export function AmountField({
  label,
  description,
  baseUnit,
  unitOf,
  amount,
  unit,
  onChange,
  error,
  required = false,
}: {
  readonly label: string;
  readonly description?: string;
  readonly baseUnit: StockBaseUnit;
  /** The item's names for a piece, so a count reads "cans" rather than "each". */
  readonly unitOf?: Pick<StockItem, 'baseUnit' | 'unitName' | 'unitNamePlural'>;
  readonly amount: string;
  readonly unit: DisplayUnit;
  readonly onChange: (amount: string, unit: DisplayUnit) => void;
  readonly error?: string | undefined;
  readonly required?: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const units = inputUnitsFor(baseUnit);
  return (
    <Field label={label} description={description} error={error} required={required} showOptional={!required}>
      {(control) => (
        <span className="console-stock-qty__loose">
          <Input
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            required={control.required}
            numeric
            className="console-stock-qty__input"
            value={amount}
            onChange={(event) => onChange(event.target.value, unit)}
          />
          {units.length > 1 ? (
            <Select className="console-stock-qty__unit" aria-label={t('stock.quantity.unitOf', { name: label })} value={unit} onValueChange={(value) => onChange(amount, value as DisplayUnit)} options={units.map((one) => ({ value: one, label: stock.unitName(one, unitOf) }))} />
          ) : (
            <span className="console-stock-qty__unit-word">{stock.unitName(unit, unitOf)}</span>
          )}
        </span>
      )}
    </Field>
  );
}

/** The item's pack sizes: a name, what it holds in the natural unit, and its own barcode. */
function PacksEditor({
  unit,
  packs,
  onChange,
  error,
}: {
  readonly unit: Pick<StockItem, 'baseUnit' | 'unitName' | 'unitNamePlural'>;
  readonly packs: readonly PackDraft[];
  readonly onChange: (packs: readonly PackDraft[]) => void;
  readonly error?: string | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const baseUnit = unit.baseUnit;
  const units = inputUnitsFor(baseUnit);
  const update = (key: string, patch: Partial<PackDraft>) => onChange(packs.map((pack) => (pack.key === key ? { ...pack, ...patch } : pack)));
  return (
    <fieldset className="console-stock-packs">
      <legend className="clav-field__label">{t('stock.editor.packs')}</legend>
      <p className="console-subtle">{t('stock.editor.packsHint')}</p>
      {packs.length === 0 ? null : (
        <ul className="console-stock-packs__list">
          {packs.map((pack, index) => {
            const holds = parseQuantity(pack.amount, pack.unit);
            return (
              <li key={pack.key} className="console-stock-packs__row">
                <Input aria-label={t('stock.editor.packName', { n: index + 1 })} placeholder={t('stock.editor.packNamePlaceholder')} value={pack.name} onChange={(event) => update(pack.key, { name: event.target.value })} />
                <span className="console-stock-qty__loose">
                  <Input numeric className="console-stock-qty__input" aria-label={t('stock.editor.packHolds', { n: index + 1 })} placeholder="0" value={pack.amount} onChange={(event) => update(pack.key, { amount: event.target.value })} />
                  {units.length > 1 ? (
                    <Select className="console-stock-qty__unit" aria-label={t('stock.editor.packUnit', { n: index + 1 })} value={pack.unit} onValueChange={(value) => update(pack.key, { unit: value as DisplayUnit })} options={units.map((one) => ({ value: one, label: stock.unitName(one, unit) }))} />
                  ) : (
                    <span className="console-stock-qty__unit-word">{stock.unitName(pack.unit, unit)}</span>
                  )}
                </span>
                <Input numeric inputMode="numeric" aria-label={t('stock.editor.packBarcode', { n: index + 1 })} placeholder={t('stock.editor.packBarcodePlaceholder')} value={pack.barcode} onChange={(event) => update(pack.key, { barcode: event.target.value })} />
                <Button type="button" variant="ghost" size="sm" icon={<Icon name="close" />} label={t('stock.editor.packRemove', { n: index + 1 })} onClick={() => onChange(packs.filter((one) => one.key !== pack.key))} />
                <span className="console-subtle console-stock-packs__equals console-num">
                  {holds === null || pack.name.trim() === '' ? '' : t('stock.editor.packEquals', { name: pack.name.trim(), size: stock.quantity(holds, unit) })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {error !== undefined ? (
        <p className="clav-field__error" role="alert">
          {error}
        </p>
      ) : null}
      <div>
        <Button type="button" variant="secondary" size="sm" icon={<Icon name="plus" />} onClick={() => onChange([...packs, { key: nextKey(), name: '', amount: '', unit: defaultUnit(baseUnit), barcode: '' }])}>
          {t('stock.editor.packAdd')}
        </Button>
      </div>
    </fieldset>
  );
}
